import { v4 as uuidv4 } from 'uuid';
import dateUtil from '../../../../commons/utils/date';
import { getSubscriptionsAPI } from './api';
import refreshProfileCollections from './refreshProfileCollections';

// Drops any existing entry with a matching id so re-merging never
// duplicates a subscription; ids left alone if missing.
function dropIncoming(existing, incomingIds) {
    return existing.filter((s) => !(s?.id != null && incomingIds.has(s.id)));
}

export function buildSubscriptionMaps(allSubs) {
    const connectedSchedules = {};
    const subscriptionDetails = {};

    allSubs.forEach((sub) => {
        connectedSchedules[sub.id] = sub.subscribedSchedules || [];

        subscriptionDetails[sub.id] = {
            publisher:       sub.publisher || {},
            app:             sub.applications?.[0] || {},
            timezone:        sub.location?.timezone || '',
            lastSyncedLabel: dateUtil.timeAgo(sub.lastSynced),
            isActive:        sub.isActive,
        };
    });

    return { connectedSchedules, subscriptionDetails };
}

async function getSubscriptions({ silent = false } = {}) {
    const session = this.env.session;
    const traceId = this.env.traceId;

    if (!silent) this.startSubscriptionProgress();

    const res = await getSubscriptionsAPI({
        store: this,
        token: session.token,
        traceId,
        page: this.page,
    });

    if (!silent) this.stopSubscriptionProgress();

    if (res.error) return;

    if (res?.data?.data?.results) {
        const { active, inactive } = this.selectActiveInactive(res.data.data.results);

        const incomingIds = new Set([...active, ...inactive].filter((s) => s?.id != null).map((s) => s.id));

        this.subscriptions = [...dropIncoming(this.subscriptions, incomingIds), ...active]
            // Belt-and-braces: an id pending unsubscribe can never end up
            // in the active list, no matter where it came from.
            .filter((s) => !this.pendingUnsubscribeIds.has(s.id));
        this.inactiveSubscriptions = [...dropIncoming(this.inactiveSubscriptions, incomingIds), ...inactive];

        const freshForMaps = [...active, ...inactive].filter((s) => !this.pendingUnsubscribeIds.has(s.id));
        const { connectedSchedules, subscriptionDetails } = buildSubscriptionMaps(freshForMaps);

        this.connectedSchedules = { ...this.connectedSchedules, ...connectedSchedules };
        this.subscriptionDetails = { ...this.subscriptionDetails, ...subscriptionDetails };

        this.pageInfo = {
            currentPage: res.data.data.currentPage,
            totalPages: res.data.data.totalPages,
            totalRecords: res.data.data.totalRecords,
            totalLoaded: this.subscriptions.length + this.inactiveSubscriptions.length,
        };

        refreshProfileCollections(this.subscriptions);

        this.observed.subscriptions = uuidv4();
        this.env.profileStore.updateConnectedDevices(res.data.data.results);
    }
}

export default getSubscriptions;
